import React, { createContext, useContext, useState, useCallback, useEffect } from "react";

/**
 * SidebarContext - Verwaltet den Sidebar-State (Mobile-Overlay + Viewport-Erkennung)
 */

interface SidebarContextType {
  /** Gibt an, ob die Sidebar bereits auf App-Level gerendert wird */
  hasSidebar: boolean;
  /** Mobile: Ist die Sidebar geöffnet? */
  isMobileOpen: boolean;
  /** Mobile: Sidebar öffnen/schließen */
  toggleMobileSidebar: () => void;
  /** Mobile: Sidebar öffnen */
  openMobileSidebar: () => void;
  /** Mobile: Sidebar schließen */
  closeMobileSidebar: () => void;
  /** Ist der Viewport im Mobile-Bereich? */
  isMobile: boolean;
}

const defaultContext: SidebarContextType = {
  hasSidebar: false,
  isMobileOpen: false,
  toggleMobileSidebar: () => {},
  openMobileSidebar: () => {},
  closeMobileSidebar: () => {},
  isMobile: false,
};

const SidebarContext = createContext<SidebarContextType>(defaultContext);

const MOBILE_BREAKPOINT = 1280; // Tablet (auch Querformat) + Mobile: Overlay-Sidebar mit Trigger. Ab xl (>=1280px) persistente Desktop-Sidebar.

export const SidebarProvider: React.FC<{
  children: React.ReactNode;
  hasSidebar: boolean;
}> = ({ children, hasSidebar }) => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  // Initial: Korrekt basierend auf aktuellem Viewport (verhindert Flash)
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth < MOBILE_BREAKPOINT;
    }
    return false;
  });

  // Viewport-Größe überwachen
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    // Initial check
    checkMobile();

    // Listener für Resize
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Sidebar schließen wenn von Mobile zu Desktop gewechselt wird
  useEffect(() => {
    if (!isMobile && isMobileOpen) {
      setIsMobileOpen(false);
    }
  }, [isMobile, isMobileOpen]);

  // Body-Scroll sperren wenn Mobile-Sidebar offen
  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileOpen]);

  const toggleMobileSidebar = useCallback(() => {
    setIsMobileOpen((prev) => !prev);
  }, []);

  const openMobileSidebar = useCallback(() => {
    setIsMobileOpen(true);
  }, []);

  const closeMobileSidebar = useCallback(() => {
    setIsMobileOpen(false);
  }, []);

  return (
    <SidebarContext.Provider
      value={{
        hasSidebar,
        isMobileOpen,
        toggleMobileSidebar,
        openMobileSidebar,
        closeMobileSidebar,
        isMobile,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};

/**
 * Hook um den Sidebar-Context zu nutzen
 */
export const useSidebarContext = (): SidebarContextType => {
  return useContext(SidebarContext);
};

/**
 * Legacy-Hook für Backwards-Compatibility
 * @deprecated Nutze useSidebarContext() stattdessen
 */
export const useHasSidebar = (): boolean => {
  const { hasSidebar } = useContext(SidebarContext);
  return hasSidebar;
};
